
import { useState, useEffect, useCallback, useRef } from 'react';
import { MCPService } from '@/types';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

// Create a module-level variable to track if error toast was shown across renders
let errorToastShown = false;

export const useMCPServices = () => {
  const [services, setServices] = useState<MCPService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userApiKeys, setUserApiKeys] = useState<Record<string, string>>({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const retryTimerRef = useRef<number | null>(null);
  const maxRetries = 2; // Limit retries to avoid flooding
  
  const { user } = useAuth();
  const { 
    fetchMCPServices, 
    addMCPService, 
    updateMCPService, 
    deleteMCPService,
    getUserMCPApiKey,
    setUserMCPApiKey
  } = useSupabaseStore();

  const loadServices = useCallback(async () => {
    if (!user) return;
    
    // Don't attempt to load if we've exceeded max retries
    if (loadAttempts >= maxRetries) {
      if (!errorToastShown) {
        toast({
          title: "Service Unavailable",
          description: "MCP services could not be loaded. Please try again later.",
          variant: "destructive"
        });
        errorToastShown = true;
      }
      setLoading(false);
      return;
    }
    
    try {
      // Only show loading on initial load
      if (!initialLoadComplete) {
        setLoading(true);
      }
      
      setError(null);
      const data = await fetchMCPServices();
      
      // Only update services if we got data
      if (Array.isArray(data)) {
        setServices(data);
        
        // Load API keys for each service if user is logged in
        if (user) {
          const keys: Record<string, string> = {};
          
          for (const service of data) {
            try {
              const apiKey = await getUserMCPApiKey(service.id);
              if (apiKey) {
                keys[service.id] = apiKey;
              }
            } catch (keyError) {
              console.error('Error loading API key for service:', service.id, keyError);
            }
          }
          
          setUserApiKeys(keys);
        }

        // Reset toast flag and load attempts on successful load
        errorToastShown = false;
        setLoadAttempts(0);
      }
      
      // Mark initial load as complete
      setInitialLoadComplete(true);
    } catch (error) {
      console.error('Error loading MCP services:', error);
      setError('Failed to load MCP services. Please try again later.');
      
      // Increase load attempts
      setLoadAttempts(prev => prev + 1);
      
      // Only show toast once and only after failures
      if (!errorToastShown) {
        toast({
          title: "Service Unavailable",
          description: "MCP services could not be loaded. Please try again later.",
          variant: "destructive"
        });
        errorToastShown = true;
      }
    } finally {
      setLoading(false);
    }
  }, [fetchMCPServices, getUserMCPApiKey, user, initialLoadComplete, loadAttempts]);

  const addService = useCallback(async (service: Omit<MCPService, "id">) => {
    try {
      const serviceId = await addMCPService(service);
      if (serviceId) {
        await loadServices();
        return serviceId;
      }
      return null;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add MCP service",
        variant: "destructive"
      });
      return null;
    }
  }, [addMCPService, loadServices]);

  const updateService = useCallback(async (id: string, updates: Partial<MCPService>) => {
    try {
      await updateMCPService(id, updates);
      // Update local state optimistically instead of fetching all services again
      setServices(prevServices => 
        prevServices.map(service => 
          service.id === id ? { ...service, ...updates } : service
        )
      );
      
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update MCP service",
        variant: "destructive"
      });
      return false;
    }
  }, [updateMCPService]);

  const removeService = useCallback(async (id: string) => {
    try {
      await deleteMCPService(id);
      // Update local state immediately instead of fetching all services again
      setServices(prevServices => prevServices.filter(service => service.id !== id));
      
      // Clean up API key from state
      setUserApiKeys(prev => {
        const newKeys = { ...prev };
        delete newKeys[id];
        return newKeys;
      });
      
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete MCP service",
        variant: "destructive"
      });
      return false;
    }
  }, [deleteMCPService]);

  const getApiKey = useCallback((serviceId: string): string => {
    return userApiKeys[serviceId] || '';
  }, [userApiKeys]);

  const setApiKey = useCallback(async (serviceId: string, apiKey: string) => {
    try {
      await setUserMCPApiKey(serviceId, apiKey);
      setUserApiKeys(prev => ({
        ...prev,
        [serviceId]: apiKey
      }));
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save API key",
        variant: "destructive"
      });
      return false;
    }
  }, [setUserMCPApiKey]);

  const isServiceActivated = useCallback((serviceId: string): boolean => {
    return Boolean(userApiKeys[serviceId]);
  }, [userApiKeys]);

  useEffect(() => {
    if (user) {
      loadServices();
    } else {
      // Reset states when user logs out
      setServices([]);
      setUserApiKeys({});
      setLoading(false);
      setError(null);
    }
  }, [loadServices, user]);

  useEffect(() => {
    // Clear existing timer if there is one
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    // Only retry if error exists AND we haven't exceeded max retries
    if (error && loadAttempts < maxRetries) {
      // Linear backoff: 5 seconds * attempt number
      const retryDelay = 5000 * (loadAttempts + 1); 
      
      console.log(`Scheduling retry attempt ${loadAttempts + 1} in ${retryDelay/1000} seconds`);
      
      // Use a ref to store the timer ID for cleanup
      retryTimerRef.current = window.setTimeout(() => {
        if (user) {
          console.log(`Executing retry attempt ${loadAttempts + 1}`);
          loadServices();
        }
        retryTimerRef.current = null;
      }, retryDelay);
    }
    
    // Cleanup function to clear timer when component unmounts
    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, [error, loadAttempts, loadServices, user]);

  return {
    services,
    loading,
    error,
    addService,
    updateService,
    removeService,
    getApiKey,
    setApiKey,
    isServiceActivated,
    refreshServices: loadServices
  };
};
